import { NextSeo } from 'next-seo';
import { memo, useCallback } from 'react';

import { LockKeyholeIcon } from '@air/design-icons';

import { PasswordPageBackground } from '~/components/PasswordPage/PasswordPageBackground';
import { PasswordPageHeader } from '~/components/PasswordPage/PasswordPageHeader/PasswordPageHeader';
import { PasswordPageInput } from '~/components/PasswordPage/PasswordPageInput/PasswordPageInput';
import { getShareSeoDescription } from '~/swr-hooks/shortUrl/utils';
import { getKeyForShortIDVerificationStatus } from '~/utils/auth/SessionHelpers';

export type PasswordPageProps = {
  shortId: string;
  onVerified: () => Promise<void>;
};

const PASSWORD_PAGE_TITLE = 'This link is private';

export const PasswordPage = memo(({ shortId, onVerified }: PasswordPageProps) => {
  const onVerifiedHandler = useCallback(async () => {
    window.sessionStorage.setItem(getKeyForShortIDVerificationStatus(shortId), 'verified');
    await onVerified();
  }, [onVerified, shortId]);

  return (
    <div className="relative flex h-screen items-center justify-center overflow-hidden">
      <NextSeo nofollow noindex title={PASSWORD_PAGE_TITLE} description={getShareSeoDescription()} />
      <PasswordPageBackground />
      <PasswordPageHeader />
      <div className="m-auto w-full max-w-[350px] p-6">
        <LockKeyholeIcon className="ml-[-5px] w-8 ads:text-icon-default text-gray-10" />
        <div className="mt-6 font-semibold ads:text-primary text-24 text-gray-12">{PASSWORD_PAGE_TITLE}</div>
        <div className="my-4 ads:text-secondary text-16 text-gray-11">Please enter the passcode to view</div>
        <PasswordPageInput shortId={shortId} onVerified={onVerifiedHandler} />
      </div>
    </div>
  );
});

PasswordPage.displayName = 'PasswordPage';
